/** @jsx jsx */

import { DateFilter } from "@cartographerio/atlas-map";
import {
  dateToTimestamp,
  epochTimestamp,
  timestampToDate,
} from "@cartographerio/types";
import { chakra, Flex } from "@chakra-ui/react";
import { css, jsx } from "@emotion/react";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import LinkButton from "../LinkButton";
import InspectorBox from "./InspectorBox";
import InspectorHeading from "./InspectorHeading";

export interface FilterPanelProps {
  onChange?: (dateFilter: DateFilter) => void;
}

const styles = {
  dateInput: css`
    display: flex;
    flex-basis: 40%;
    width: 100%;
    border-radius: 3px;
    border: 1px solid lightgray;
    border-radius: 2px;
    padding: 0.25em;
  `,
};

type FilterType = "all" | "last_week" | "last_30_days" | "last_year" | "custom";

function calculateFilter(
  filterType: FilterType,
  filter: DateFilter
): DateFilter {
  const DAY_IN_MILLIS = 24 * 60 * 60 * 1000;

  switch (filterType) {
    case "all":
      return { from: null, to: null };

    case "last_week":
      return {
        from: epochTimestamp(Date.now() - 7 * DAY_IN_MILLIS),
        to: null,
      };

    case "last_30_days":
      return {
        from: epochTimestamp(Date.now() - 30 * DAY_IN_MILLIS),
        to: null,
      };

    case "last_year":
      return {
        from: epochTimestamp(Date.now() - 365 * DAY_IN_MILLIS),
        to: null,
      };

    case "custom":
      return filter;
  }
}

export default function FilterPanel(props: FilterPanelProps): ReactElement {
  const { onChange } = props;

  const [filterType, setFilterType] = useState<FilterType>("all");
  const [filter, setFilter] = useState<DateFilter>({ from: null, to: null });

  const fromDate = useMemo(() => {
    return filter.from == null ? undefined : timestampToDate(filter.from);
  }, [filter.from]);

  const toDate = useMemo(() => {
    return filter.to == null ? undefined : timestampToDate(filter.to);
  }, [filter.to]);

  const updateFilter = useCallback(
    (filterType: FilterType, filter: DateFilter) => {
      setFilterType(filterType);
      setFilter(calculateFilter(filterType, filter));
    },
    []
  );

  useEffect(() => {
    onChange?.(filter);
  }, [onChange, filter]);

  return (
    <InspectorBox pt="2" pb="4">
      <InspectorHeading>Date Filter</InspectorHeading>

      <Flex w="100%" px="2" flexDirection="row" justifyContent="center">
        <LinkButton
          fontSize="sm"
          selected={filterType === "all"}
          onClick={() => updateFilter("all", filter)}
        >
          All
        </LinkButton>

        <LinkButton
          fontSize="sm"
          selected={filterType === "last_week"}
          onClick={() => updateFilter("last_week", filter)}
        >
          7 days
        </LinkButton>

        <LinkButton
          fontSize="sm"
          selected={filterType === "last_30_days"}
          onClick={() => updateFilter("last_30_days", filter)}
        >
          30 days
        </LinkButton>

        <LinkButton
          fontSize="sm"
          selected={filterType === "last_year"}
          onClick={() => updateFilter("last_year", filter)}
        >
          12 months
        </LinkButton>

        <LinkButton
          fontSize="sm"
          selected={filterType === "custom"}
          onClick={() => updateFilter("custom", filter)}
        >
          Custom
        </LinkButton>
      </Flex>

      {filterType === "custom" && (
        <Flex
          w="100%"
          px="4"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
        >
          <DatePicker
            css={styles.dateInput}
            selected={fromDate}
            onChange={from =>
              updateFilter("custom", {
                ...filter,
                from: from == null ? null : dateToTimestamp(from),
              })
            }
            selectsStart
            startDate={fromDate}
            endDate={toDate}
            dateFormat="dd/mm/yyyy"
            placeholderText={"dd/mm/yyyy"}
          />

          <chakra.span mx="2">to</chakra.span>

          <DatePicker
            css={styles.dateInput}
            selected={toDate}
            onChange={to =>
              updateFilter("custom", {
                ...filter,
                to: to == null ? null : dateToTimestamp(to),
              })
            }
            selectsEnd
            startDate={fromDate}
            endDate={toDate}
            dateFormat="dd/mm/yyyy"
            minDate={fromDate}
            placeholderText={"dd/mm/yyyy"}
          />
        </Flex>
      )}
    </InspectorBox>
  );
}
